import { useAtom } from '@reatom/npm-react'
import { memo } from 'react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { NumericFormat } from '@blade/std-lib'
import { KnownPlatform } from '@root/entities/network'
import { KnownPlatformIcon } from '@root/entities/network/ui/known-platform/known-platform-icon'
import { usePairAgeFormatted } from '@root/entities/pair'
import { FlashTextContentDistinct } from '@root/features/flash/flash-text-content-distinct'
import {
  BackingIcon,
  DevSoldIcon,
  DevBoughtIcon,
  ReservedSpace,
  TooltipCard,
} from '@root/shared/ui-kit'

import {
  ExploreDevHoldingsTooltip,
  ExploreDevSwapTooltip,
} from '../../explore-dev-tooltip'
import { PairExploreItemAuditItem } from '../pair-explore-item-audit-item'

import type { PairModel } from '@root/entities/pair'
import type { TradeType } from '@root/entities/trade-type'
import type { IconProps } from '@root/shared/ui-kit'

type Props = {
  pair: PairModel
  size: number
}

export const PairExploreItemAuditDevHolding = memo(({ pair, size }: Props) => {
  const [txCreator] = useAtom(pair.initTxCreator)
  const [audit] = useAtom(pair.audit)
  const [devSoldAll] = useAtom(pair.devSoldAllAtom)
  const { formatted: devWalletFundedAt } = usePairAgeFormatted(
    txCreator?.funding?.at,
  )

  const devHoldPercentage = useMemo(
    () =>
      NumericFormat.formatPreset(
        audit.checks.ownerTokenBalance.percent,
        'percents',
      ),
    [audit.checks.ownerTokenBalance.percent],
  )

  const knownPlatform = useMemo(() => {
    const address = txCreator?.funding?.by
    if (!address) {
      return undefined
    }
    return KnownPlatform.tryGetKnownPlatform(address)
  }, [txCreator?.funding?.by])

  const hasTrades = txCreator?.lastTx?.operation !== undefined
  const Cmp = hasTrades ? ExploreDevSwapTooltip : ExploreDevHoldingsTooltip

  const renderDevPurchase = () => {
    if (!txCreator?.lastTx?.operation) {
      return undefined
    }

    const { Icon } = PURCHASE_MAP[txCreator.lastTx.operation]

    return (
      <DevTooltipCard
        closeArea={<Cmp.CloseArea pair={pair} />}
        clickArea={<Cmp.ClickArea pair={pair} />}
        sideOffset={5}
      >
        <AuditItem
          pair={pair}
          data-sold-all={devSoldAll}
          intent={audit.checks.ownerTokenBalance.status}
        >
          <Icon size={size} />{' '}
          {audit.checks.ownerTokenBalance.percent > 0 ? (
            <FlashTextContentDistinct>
              {devHoldPercentage}%
            </FlashTextContentDistinct>
          ) : undefined}
        </AuditItem>
      </DevTooltipCard>
    )
  }

  return (
    <>
      <DevTooltipCard
        closeArea={<Cmp.CloseArea pair={pair} />}
        clickArea={<Cmp.ClickArea pair={pair} />}
        sideOffset={5}
      >
        <AuditItem
          pair={pair}
          data-sold-all={devSoldAll}
          intent={audit.checks.ownerTokenBalance.status}
        >
          <BackingIcon size={size} />{' '}
          {devWalletFundedAt ? (
            <ReservedSpace $chars={devWalletFundedAt.length}>
              {devWalletFundedAt}
            </ReservedSpace>
          ) : undefined}
          {knownPlatform ? (
            <KnownPlatformIcon size={size} knownPlatform={knownPlatform} />
          ) : undefined}
        </AuditItem>
      </DevTooltipCard>

      {renderDevPurchase()}
    </>
  )
})

const AuditItem = styled(PairExploreItemAuditItem)`
  &[data-sold-all='true'] {
    border: 1px solid var(--primaryA-3);
    background: var(--primaryA-1);
    color: var(--primary-9);
    &:hover {
      border: 1px solid var(--primaryA-4);
      background: var(--primaryA-3);
    }
  }
`

const PURCHASE_MAP: Record<
  NonNullable<TradeType.SwapDirection>,
  {
    Icon: React.FC<IconProps>
  }
> = {
  buy: {
    Icon: DevBoughtIcon,
  },
  sell: {
    Icon: DevSoldIcon,
  },
}

const DevTooltipCard = styled(TooltipCard)`
  html[lang='ru'] & {
    --tooltip-width: 250px;
  }
`
